// src/pages/AddResourcePage.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, GeoPoint, getDocs } from 'firebase/firestore';
import './AddResourcePage.css';
import { useJsApiLoader } from '@react-google-maps/api';
import { useAuth } from '../context/AuthContext';
import geohash from 'ngeohash';

const CustomAlertModal = ({ message, onClose, onConfirm }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (dontShowAgain) {
      localStorage.setItem('hasDismissedGpsAlert', 'true');
    }
    onConfirm();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Location Data Found!</h2>
        <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        <div className="modal-checkbox">
          <input
            type="checkbox"
            id="dontShowAgain"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <label htmlFor="dontShowAgain">Don't show this message again</label>
        </div>
        <div className="modal-actions">
          <button className="btn-confirm-delete" onClick={handleConfirm}>OK</button>
        </div>
      </div>
    </div>
  );
};

const CheckboxGroup = ({ title, options, selectedOptions, handleSelect }) => {
  return (
    <div className="checkbox-group-container">
      <p className="checkbox-group-title">{title}</p>
      <div className="checkbox-grid">
        {options.map((option) => (
          <label key={option.id} className="checkbox-label">
            <input
              type="checkbox"
              value={option.id}
              checked={selectedOptions.includes(option.id)}
              onChange={() => handleSelect(option.id)}
            />
            {option.name}
          </label>
        ))}
      </div>
    </div>
  );
};

function AddResourcePage() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['geocoding'],
  });

  const { currentUser } = useAuth(); 

  const [organizationName, setOrganizationName] = useState('');
  const [message, setMessage] = useState('');
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [geoHashValue, setGeoHashValue] = useState(''); 
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesCollection = collection(db, 'categories');
        const categorySnapshot = await getDocs(categoriesCollection);
        const categoryList = categorySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoryList);
      } catch (error) {
        console.error("Error fetching categories: ", error);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const handleCalculateGps = () => {
    if (!location || location.trim() === '') {
      alert('Please enter a location address before calculating.');
      return;
    }
    
    setIsCalculating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      setIsCalculating(false);
      if (status === 'OK') {
        const geo = results[0].geometry.location;
        const lat = geo.lat();
        const lng = geo.lng();
        const hash = geohash.encode(lat, lng); 

        // Extract City and State from Geocoder results
        let extractedCity = '';
        let extractedState = '';
        results[0].address_components.forEach(component => {
          if (component.types.includes('locality')) {
            extractedCity = component.long_name;
          }
          if (component.types.includes('administrative_area_level_1')) {
            extractedState = component.short_name; // Gets the abbreviation like "UT"
          }
        });
        
        setLatitude(lat);
        setLongitude(lng);
        setGeoHashValue(hash); 
        setCity(extractedCity);
        setState(extractedState);
        
        const hasDismissed = localStorage.getItem('hasDismissedGpsAlert') === 'true';
        if (!hasDismissed) {
          setAlertMessage(`The following fields have been filled:\nLat: ${lat.toFixed(4)}\nLng: ${lng.toFixed(4)}\nCity: ${extractedCity}\nState: ${extractedState}\n\nPlease remember to click "Save Resource" to finish.`);
          setShowAlert(true);
        }
      } else {
        alert('Geocode was not successful: ' + status);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!currentUser) {
        alert("A user was not found, please try logging out and signing back in.");
        return;
    }

    if (!latitude || !longitude || !geoHashValue) {
      alert("Please click the 'Calculate GPS from Address' button and make sure the GPS and Geohash fields are filled before saving.");
      return;
    }

    if (selectedCategoryIds.length === 0) {
      alert("Please select at least one 'Type of Resource'.");
      return;
    }

    setIsSubmitting(true);
    try {
      const resourceData = {
        organizationName,
        message,
        location,
        city,
        state,
        geohash: geoHashValue, 
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        categoryIDs: selectedCategoryIds,
        status: currentUser.role === 'admin' ? 'approved' : 'pending',
        coordinates: new GeoPoint(parseFloat(latitude), parseFloat(longitude)), 
      };

      await addDoc(collection(db, "resources"), resourceData);
      alert('Resource saved successfully!');
      
      // Reset Form
      setOrganizationName('');
      setMessage('');
      setSelectedCategoryIds([]);
      setLocation('');
      setLatitude('');
      setLongitude('');
      setGeoHashValue('');
      setCity('');
      setState('');
    } catch (error) {
      console.error("Error adding document: ", error);
      alert(`Error saving resource: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="page-container">
      {showAlert && <CustomAlertModal message={alertMessage} onConfirm={() => setShowAlert(false)} />}
      <div className="form-container">
        <h1 className="form-header">Add a New Resource</h1>
        <form onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label htmlFor="organizationName" className="form-label">Name of your Organization</label>
            <input type="text" id="organizationName" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} className="form-input" placeholder="e.g., The Community Center" required />
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">More Details (Optional)</label>
            <textarea id="message" value={message} onChange={(e) => setMessage(e.target.value)} className="form-input" placeholder="Details..." rows="4" />
          </div>

          <div className="form-group" style={{ marginBottom: '24px' }}>
            <CheckboxGroup title="Type of Resource (select all that apply)" options={categories} selectedOptions={selectedCategoryIds} handleSelect={handleCategoryChange} />
          </div>
          
          <div className="form-group">
            <label htmlFor="location" className="form-label">Location Address</label>
            <input type="text" id="location" value={location} onChange={(e) => setLocation(e.target.value)} className="form-input" placeholder="Address..." required/>
          </div>

          <div className="form-group-action">
            <button type="button" onClick={handleCalculateGps} className="calculate-gps-button" disabled={!isLoaded || isCalculating}>
              {isCalculating ? 'Calculating...' : 'Calculate GPS & Geohash'}
            </button>
          </div>

          <div className="coordinate-group">
            <div className="form-group">
              <label htmlFor="city" className="form-label">City</label>
              <input type="text" id="city" value={city} readOnly className="form-input read-only-input" placeholder="Auto-calculated" />
            </div>
            <div className="form-group">
              <label htmlFor="state" className="form-label">State</label>
              <input type="text" id="state" value={state} readOnly className="form-input read-only-input" placeholder="Auto-calculated" />
            </div>
          </div>

          <div className="coordinate-group">
            <div className="form-group">
              <label htmlFor="latitude" className="form-label">Latitude</label>
              <input type="number" step="any" id="latitude" value={latitude} readOnly className="form-input read-only-input" placeholder="Auto-calculated" />
            </div>
            <div className="form-group">
              <label htmlFor="longitude" className="form-label">Longitude</label>
              <input type="number" step="any" id="longitude" value={longitude} readOnly className="form-input read-only-input" placeholder="Auto-calculated" />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="geohash" className="form-label">Geohash (Generated)</label>
            <input type="text" id="geohash" value={geoHashValue} readOnly className="form-input read-only-input" placeholder="Click calculate to generate..." />
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Resource'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddResourcePage;